/**
 * KAN-826: background job of "Дополнить уже загруженных" and of its preview.
 *
 * The upload (or the preview request with dry_run) answers 202 {job_id}; the
 * page calls `start(jobId, mode, importResult)` and this hook polls
 * GET /datasets/{id}/upload-jobs/{job_id}/ until the job is done or failed.
 * Every field of the job endpoint is read here only (normalized into
 * `UploadUpdateJobView`), so a contract change touches this file alone.
 */
import { useCallback, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { extractErrorMessage } from '@/lib/extractErrorMessage';
import { formatUploadSummary } from '@/lib/uploadSummary';
import { datasetsApi } from '@/services/api/datasets';
import type {
  UploadJob,
  UploadJobMerge,
  UploadJobResult,
  UploadJobSkippedMerge,
  UploadResult,
} from '@/types/dataset';

export const UPLOAD_JOB_POLL_MS = 1500;

export type UploadJobMode = 'preview' | 'run';

export interface UploadJobCounter {
  key: string;
  label: string;
  value: number;
}

export interface UploadUpdateJobView {
  mode: UploadJobMode;
  status: UploadJob['status'];
  /** 0..100 while running; null when the total is unknown. */
  percent: number | null;
  progressText: string;
  counters: UploadJobCounter[];
  /** "X -> Y, контактов N". */
  merged: string[];
  /** "X и Y: одинаковый ИНН N, названия разные, не объединены". */
  skipped: string[];
  error: string | null;
  /** Run only: summary of the new rows imported before the job; '' otherwise. */
  importSummary: string;
}

const COUNTER_LABELS: ReadonlyArray<[keyof UploadJobResult, string]> = [
  ['updated_records', 'Обновлено записей'],
  ['updated_clients', 'Обновлено клиентов'],
  ['companies_filled', 'Организаций заполнено'],
  ['companies_merged', 'Организаций объединено'],
  ['merges_skipped', 'Объединений пропущено'],
  ['not_found', 'Не найдено'],
  ['rows_skipped', 'Строк пропущено (неоднозначное совпадение)'],
];

const isActive = (status: UploadJob['status'] | undefined) =>
  status === 'pending' || status === 'running';

const mergedText = (item: UploadJobMerge): string =>
  `${item.source_name} -> ${item.target_name}, контактов ${item.contacts_moved}`;

const skippedText = (item: UploadJobSkippedMerge): string =>
  `${item.source_name} и ${item.target_name}: одинаковый ИНН ${item.inn}, названия разные, не объединены`;

export function toJobView(
  job: UploadJob,
  mode: UploadJobMode,
  importResult: UploadResult | null = null
): UploadUpdateJobView {
  const result = job.result ?? {};
  const done = job.progress?.done ?? 0;
  const total = job.progress?.total ?? 0;
  return {
    mode,
    status: job.status,
    percent: total > 0 ? Math.min(100, Math.round((done / total) * 100)) : null,
    progressText: total > 0 ? `${done} из ${total}` : '',
    counters: COUNTER_LABELS.filter(([key]) => typeof result[key] === 'number').map(
      ([key, label]) => ({ key, label, value: result[key] as number })
    ),
    merged: (result.merged ?? []).map(mergedText),
    skipped: (result.skipped ?? []).map(skippedText),
    error: job.status === 'failed' ? job.error || 'Задача завершилась с ошибкой' : null,
    importSummary: importResult ? formatUploadSummary(importResult) : '',
  };
}

export function useUploadUpdateJob(datasetId: string) {
  const [job, setJob] = useState<{
    id: string;
    mode: UploadJobMode;
    importResult: UploadResult | null;
  } | null>(null);

  const query = useQuery<UploadJob>({
    queryKey: ['datasetUploadJob', datasetId, job?.id],
    queryFn: () => datasetsApi.uploadJob(datasetId, job?.id as string),
    enabled: !!datasetId && !!job,
    retry: false,
    refetchInterval: (q) => {
      if (q.state.status === 'error') return false;
      return !q.state.data || isActive(q.state.data.status) ? UPLOAD_JOB_POLL_MS : false;
    },
  });

  const start = useCallback(
    (id: string, mode: UploadJobMode, importResult: UploadResult | null = null) =>
      setJob({ id, mode, importResult }),
    []
  );
  const reset = useCallback(() => setJob(null), []);

  let view: UploadUpdateJobView | null = null;
  if (job) {
    if (query.data) {
      view = toJobView(query.data, job.mode, job.importResult);
    } else if (query.error) {
      view = toJobView(
        {
          status: 'failed',
          error: extractErrorMessage(query.error, 'Не удалось получить состояние задачи'),
        },
        job.mode,
        job.importResult
      );
    } else {
      view = toJobView({ status: 'pending' }, job.mode, job.importResult);
    }
  }

  return {
    view,
    isRunning: !!view && isActive(view.status),
    start,
    reset,
  };
}
